import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { BackgroundWaves } from '@/components/BackgroundWaves';
import { PricingCard } from '@/components/PricingCard';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription, useCreatePortalSession } from '@/queries/subscriptions';
import type { SubscriptionTier } from '@/types/subscription';
import { apiConfig } from '@/config/api';
import styles from './pricing.module.scss';

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
});

function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: subscription } = useSubscription();
  const createPortalSession = useCreatePortalSession();

  const currentTier: SubscriptionTier = subscription?.tier || 'foundation';
  const hasPaidSubscription = currentTier !== 'foundation';

  const handleSelectTier = (tier: SubscriptionTier) => {
    if (!user) {
      navigate({ to: '/signup' });
      return;
    }

    if (tier === 'foundation') {
      navigate({ to: '/' });
      return;
    }

    // If user has an existing paid subscription and clicks on a different paid tier,
    // redirect them to the customer portal to manage their subscription
    if (hasPaidSubscription && tier !== currentTier) {
      createPortalSession.mutate();
      return;
    }

    // Navigate to checkout page with selected tier
    navigate({ to: '/checkout', search: { tier } });
  };

  const getButtonText = (tier: SubscriptionTier, tierTitle: string): string => {
    if (!user) return 'Sign Up';
    if (tier === 'foundation') return 'Get Learning';
    if (currentTier === tier) return 'Current Plan';
    // If user has a paid subscription and is viewing a different tier
    if (hasPaidSubscription) return 'Change Plan';
    return `Upgrade to ${tierTitle}`;
  };

  const pricingTiers = [
    {
      tier: 'foundation' as SubscriptionTier,
      title: 'Foundation',
      price: 0,
      features: [
        '10 quiz generations per month',
        'Up to 30 saved quizzes',
        'Basic question types',
      ],
      buttonText: getButtonText('foundation', 'Foundation'),
    },
    {
      tier: 'deep_dive' as SubscriptionTier,
      title: 'Deep Dive',
      price: 4.99,
      features: [
        '50 quiz generations per month',
        'Up to 300 saved quizzes',
        'Advanced question types',
      ],
      isPopular: true,
      buttonText: getButtonText('deep_dive', 'Deep Dive'),
    },
    {
      tier: 'mastermind' as SubscriptionTier,
      title: 'Mastermind',
      price: 7.99,
      features: [
        '100 quiz generations per month',
        'Unlimited saved quizzes',
        'All question types',
      ],
      buttonText: getButtonText('mastermind', 'Mastermind'),
    },
  ].filter(tier => {
    // In production, only show the Foundation (free) tier
    if (apiConfig.getEnvironment() === 'production') {
      return tier.tier === 'foundation';
    }
    return true;
  });

  const isProduction = apiConfig.getEnvironment() === 'production';

  return (
    <div className={styles.pricing}>
      {/* Background gradient waves */}
      <BackgroundWaves />

      <motion.div
        className={styles.pricing__header}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className={styles.pricing__title}>Choose Your Learning Path</h1>
        <p className={styles.pricing__subtitle}>
          Unlock your potential with the perfect plan for your needs
        </p>
      </motion.div>

      <div className={styles.pricing__cards}>
        {pricingTiers.map((tier) => (
          <PricingCard
            key={tier.tier}
            {...tier}
            isCurrent={currentTier === tier.tier}
            onSelect={() => handleSelectTier(tier.tier)}
          />
        ))}
      </div>

      {/* Feature comparison table */}
      <motion.div
        className={styles.pricing__comparison}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <h2 className={styles.pricing__comparisonTitle}>Feature Comparison</h2>
        <div className={styles.pricing__tableContainer}>
          <table className={styles.pricing__table}>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Foundation</th>
                {!isProduction && (
                  <>
                    <th>Deep Dive</th>
                    <th>Mastermind</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Monthly Quiz Generations</td>
                <td>10</td>
                {!isProduction && (
                  <>
                    <td>50</td>
                    <td>100</td>
                  </>
                )}
              </tr>
              <tr>
                <td>Saved Quizzes</td>
                <td>30</td>
                {!isProduction && (
                  <>
                    <td>300</td>
                    <td>Unlimited</td>
                  </>
                )}
              </tr>
              <tr>
                <td>Question Types</td>
                <td>Basic</td>
                {!isProduction && (
                  <>
                    <td>Advanced</td>
                    <td>All</td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );
}
